import { Tooltip } from 'antd';
import { useSession } from 'next-auth/react';
import { CSSProperties, Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import ShareSvg from '../../../assets/icons/ShareSvg';
import { useMetrics } from '../../../hooks/useMetrics';
import { getAggregatedSlidesAndActivities } from '../../../utils/roundUtils';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { Slide } from '../../SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../SlideComponents/slideUtil';
import {
  LiveGameActionButton,
  LiveGameActionButtonContainer,
  LiveGameNavigationActions,
} from '../LiveGameActionButtons/LiveGameActionButtons';

export const PREVIEW_CONTAINER_ID = 'preview-container';
const LiveGamePreviewActionButtons = ({
  slides,
  setShowShareModal,
  selectedSlide,
  setSelectedSlide,
  style,
}: {
  slides: Slide[];
  selectedSlide: Slide;
  style?: CSSProperties;
  setShowShareModal?: Dispatch<SetStateAction<boolean>>;
  setSelectedSlide: React.Dispatch<React.SetStateAction<Slide | undefined>>;
}) => {
  const session = useSession();
  const metrics = useMetrics();
  const { update } = useIntercom();
  const numberOfSlides = useMemo(() => {
    return getAggregatedSlidesAndActivities(slides || []).length;
  }, [slides]);

  const currentSlideRound = useMemo(() => {
    return selectedSlide
      ? findRelatedSlidesWithoutIndex(selectedSlide.id, slides)[0]
      : undefined;
  }, [selectedSlide, slides]);
  const selectedRoundIndex = useMemo(() => {
    const slideRounds = getAggregatedSlidesAndActivities(slides || []);
    return currentSlideRound ? slideRounds.indexOf(currentSlideRound) : 0;
  }, [currentSlideRound, slides]);

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });
    return () => {
      update({
        hideDefaultLauncher: false,
      });
    };
  }, [update]);

  const handleSkipBack = () => {
    if (selectedRoundIndex > 0 && selectedSlide) {
      if (session.status === 'authenticated') {
        metrics.logEvent('LiveGamePreview.GoBack.Clicked');
      }
      const currentRelatedRounds = findRelatedSlidesWithoutIndex(
        selectedSlide.id,
        slides,
      );
      const firstRelatedRound = currentRelatedRounds[0];
      const firstRelatedRoundIndex = slides.findIndex(
        round => round.id === firstRelatedRound.id,
      );
      const previousRound = slides[firstRelatedRoundIndex - 1];
      const related = findRelatedSlidesWithoutIndex(previousRound.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);
      const newRound = related[wasPlayed ? related.length - 1 : 0];

      setSelectedSlide(newRound);
    }
  };
  const handleSkipForward = () => {
    if (selectedRoundIndex + 1 < numberOfSlides && selectedSlide) {
      if (session.status === 'authenticated') {
        metrics.logEvent('LiveGamePreview.GoForward.Clicked');
      }
      const currentRelatedRounds = findRelatedSlidesWithoutIndex(
        selectedSlide.id,
        slides,
      );
      const lastRelatedRound = currentRelatedRounds[currentRelatedRounds.length - 1];
      const lastRelatedRoundIndex = slides.findIndex(
        round => round.id === lastRelatedRound.id,
      );
      const nextRound = slides[lastRelatedRoundIndex + 1];
      setSelectedSlide(nextRound);
    }
  };
  return (
    <LiveGameActionButtonContainer
      $isInvisible={false}
      style={style ? style : { position: 'absolute' }}
    >
      {setShowShareModal && <ShareAction setShowShareModal={setShowShareModal} />}
      {selectedSlide && slides && (
        <LiveGameNavigationActions
          numberOfSlides={numberOfSlides}
          currentSlideType={selectedSlide?.type}
          handleSkipBack={handleSkipBack}
          handleSkipForward={handleSkipForward}
          isNavigationDisabled={false}
          currentSlideRoundIndex={selectedRoundIndex}
        />
      )}
    </LiveGameActionButtonContainer>
  );
};

const ShareAction = ({
  setShowShareModal,
}: {
  setShowShareModal: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const session = useSession();
  return (
    <ShareActionsContainer>
      <Tooltip title={t('Open link to this lesson to share')} mouseEnterDelay={2}>
        <LiveGameActionButton
          style={{ marginLeft: '2px', display: 'flex', fontSize: '16px' }}
          onClick={() => {
            if (session.status === 'authenticated') {
              metrics.logEvent('LiveGameViewer.Share.Open');
            }
            setShowShareModal(true);
          }}
        >
          <IconWrapper height={'25px'} width={'25px'} style={{ marginRight: '10px' }}>
            <ShareSvg />
          </IconWrapper>

          {t('Share')}
        </LiveGameActionButton>
      </Tooltip>
    </ShareActionsContainer>
  );
};

const ShareActionsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
`;

export default LiveGamePreviewActionButtons;
